import {
  describeAffix,
  getItem,
  type InventoryState,
  RARITY_COLORS,
  TRADE_MAX_ITEMS,
  type TradeSide,
  type TradeStack,
  type TradeState,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { closeButton } from './hud.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { uiFormat, uiNumber, uiText } from './text.js';
import { isTouch } from './touch.js';
import './trade.css';
import { kitTitle } from './kit.js';

/**
 * The trade window (ADR 0034): my offer on the left, the other player's on
 * the right, each with its stacks and its gold, and whether each side has
 * confirmed. Items join my offer from the inventory, opened in pick mode
 * beside it; a click on one of my stacks takes it back. Every change of my
 * offer goes out whole (`onOffer`): the server replaces the offer and lets
 * both confirmations lapse. The panel redraws from the state the server
 * sends back, never from its own guess.
 */

export interface TradePanelOptions {
  icons: CssAtlas | null;
  /** My whole new offer, after a stack left it or the gold changed. */
  onOffer: (items: { itemId: string; quantity: number }[], gold: number) => void;
  onConfirm: () => void;
  /** The cross, Escape or the button: the trade ends for both. */
  onCancel: () => void;
}

/** How long a typed gold amount waits before it goes out, so each digit is not an offer. */
const GOLD_DEBOUNCE_MS = 300;

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

export class TradePanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly mine: SideView;
  private readonly theirs: SideView;
  private readonly goldInput: HTMLInputElement;
  private readonly confirm: HTMLButtonElement;
  private readonly message: HTMLElement;
  private state: NonNullable<TradeState> | null = null;
  private goldTimer: number | undefined;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.options.onCancel();
  };

  constructor(private readonly options: TradePanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'trade';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Échange'));

    this.title = document.createElement('h2');
    this.title.className = 'trade__title';
    kitTitle(this.title, 'trade');
    const close = closeButton('Annuler (Échap)', () => {
      this.options.onCancel();
    });

    const columns = document.createElement('div');
    columns.className = 'trade__columns';
    this.mine = new SideView(options.icons, (itemId) => {
      this.removeStack(itemId);
    });
    this.theirs = new SideView(options.icons, null);

    // My gold: typed, sent after a pause or on Enter, clamped to my purse.
    const gold = document.createElement('label');
    gold.className = 'trade__gold';
    const goldLabel = document.createElement('span');
    goldLabel.className = 'trade__gold-label';
    goldLabel.textContent = uiText('Or :');
    this.goldInput = document.createElement('input');
    this.goldInput.className = 'trade__gold-input';
    this.goldInput.type = 'number';
    this.goldInput.min = '0';
    this.goldInput.step = '1';
    this.goldInput.inputMode = 'numeric';
    this.goldInput.value = '0';
    this.goldInput.addEventListener('input', () => {
      window.clearTimeout(this.goldTimer);
      this.goldTimer = window.setTimeout(() => {
        this.sendGold();
      }, GOLD_DEBOUNCE_MS);
    });
    this.goldInput.addEventListener('blur', () => {
      this.sendGold();
    });
    this.goldInput.addEventListener('keydown', (event) => {
      // The window's keys (inventory, map…) must not fire while typing a number.
      event.stopPropagation();
      if (event.key === 'Enter') {
        event.preventDefault();
        this.sendGold();
        this.goldInput.blur();
      }
    });
    gold.append(goldLabel, this.goldInput);
    this.mine.root.append(gold);
    columns.append(this.mine.root, this.theirs.root);

    const actions = document.createElement('div');
    actions.className = 'trade__actions';
    this.confirm = document.createElement('button');
    this.confirm.type = 'button';
    this.confirm.className = 'trade__button';
    this.confirm.addEventListener('click', () => {
      if (this.state?.mine.confirmed === true) return;
      this.options.onConfirm();
    });
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'trade__button trade__button--cancel';
    cancel.textContent = uiText('Annuler');
    cancel.addEventListener('click', () => {
      this.options.onCancel();
    });
    actions.append(this.confirm, cancel);

    this.message = document.createElement('p');
    this.message.className = 'trade__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(this.title, close, columns, actions, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Draw the trade as the server sees it; `null` closes the window. */
  show(state: TradeState, myBag: InventoryState): void {
    if (state === null) {
      this.close();
      return;
    }
    const wasOpen = !this.root.hidden;
    this.state = state;
    this.root.hidden = false;
    this.title.textContent = uiFormat('Échange avec {name}', { name: state.partnerName });
    this.mine.render('Moi', state.mine, state.mine.confirmed ? 'Validé' : '');
    this.theirs.render(
      state.partnerName,
      state.theirs,
      state.theirs.confirmed ? 'A validé' : 'En attente',
    );
    this.mine.status.dataset.confirmed = String(state.mine.confirmed);
    this.theirs.status.dataset.confirmed = String(state.theirs.confirmed);
    this.mine.hint(
      state.mine.items.length >= TRADE_MAX_ITEMS
        ? uiFormat('{max} piles au plus : retire un objet pour en ajouter un autre.', {
            max: TRADE_MAX_ITEMS,
          })
        : isTouch()
          ? 'Touche un objet de ton sac, puis « Tout » pour la pile entière. Touche une pile ici pour la reprendre.'
          : "Clique un objet de ton sac pour l'ajouter (Maj : toute la pile). Clique une pile ici pour la reprendre.",
      state.mine.items.length >= TRADE_MAX_ITEMS ? 'full' : 'ok',
    );
    this.goldInput.max = String(myBag.gold);
    // A number being typed is not overwritten by the echo of the last one sent.
    if (document.activeElement !== this.goldInput) {
      this.goldInput.value = String(state.mine.gold);
    }
    this.confirm.textContent = uiText(state.mine.confirmed ? 'Validé ✓' : 'Valider');
    this.confirm.classList.toggle('trade__button--confirmed', state.mine.confirmed);
    this.confirm.disabled = state.mine.confirmed;
    if (!wasOpen) this.message.textContent = '';
  }

  /** A word under the buttons (a refusal): the last one only. */
  say(text: string): void {
    this.message.textContent = uiText(text);
  }

  close(): void {
    window.clearTimeout(this.goldTimer);
    this.goldTimer = undefined;
    this.state = null;
    this.root.hidden = true;
    this.message.textContent = '';
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    window.clearTimeout(this.goldTimer);
    this.root.remove();
  }

  private currentItems(): { itemId: string; quantity: number }[] {
    return (this.state?.mine.items ?? []).map(({ itemId, quantity }) => ({ itemId, quantity }));
  }

  private removeStack(itemId: string): void {
    if (this.state === null) return;
    this.options.onOffer(
      this.currentItems().filter((stack) => stack.itemId !== itemId),
      this.state.mine.gold,
    );
  }

  private sendGold(): void {
    window.clearTimeout(this.goldTimer);
    this.goldTimer = undefined;
    if (this.state === null) return;
    const purse = Number(this.goldInput.max);
    const typed = Math.floor(Number(this.goldInput.value));
    const gold = Number.isFinite(typed) ? Math.min(Math.max(typed, 0), purse) : 0;
    this.goldInput.value = String(gold);
    if (gold === this.state.mine.gold) return;
    this.options.onOffer(this.currentItems(), gold);
  }
}

/** One column: whose it is, whether they confirmed, their stacks and their gold. */
class SideView {
  readonly root: HTMLElement;
  readonly status: HTMLElement;
  private readonly who: HTMLElement;
  private readonly list: HTMLElement;
  private readonly hintLine: HTMLElement;
  private readonly goldLine: HTMLElement;

  constructor(
    private readonly icons: CssAtlas | null,
    /** Set on my side: a click on a stack takes it back. */
    private readonly onRemove: ((itemId: string) => void) | null,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'trade__side';
    const head = document.createElement('div');
    head.className = 'trade__head';
    this.who = document.createElement('h3');
    this.who.className = 'trade__who';
    this.status = document.createElement('span');
    this.status.className = 'trade__status';
    head.append(this.who, this.status);
    this.list = document.createElement('ul');
    this.list.className = 'trade__list';
    this.hintLine = document.createElement('p');
    this.hintLine.className = 'trade__hint';
    this.hintLine.hidden = onRemove === null;
    this.goldLine = document.createElement('p');
    this.goldLine.className = 'trade__gold trade__gold-value';
    // The other's gold is read; mine is typed in the input the panel appends.
    this.goldLine.hidden = onRemove !== null;
    this.root.append(head, this.list, this.hintLine, this.goldLine);
  }

  hint(text: string, tone: 'ok' | 'full'): void {
    this.hintLine.textContent = uiText(text);
    this.hintLine.dataset.tone = tone;
  }

  render(name: string, side: TradeSide, status: string): void {
    this.who.textContent = uiText(name);
    this.who.title = uiText(name);
    this.status.textContent = uiText(status);
    this.list.replaceChildren();
    if (side.items.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'trade__empty';
      empty.textContent = uiText('Rien pour l’instant.');
      this.list.append(empty);
    }
    for (const stack of side.items) {
      const row = document.createElement('li');
      row.append(this.stackElement(stack));
      this.list.append(row);
    }
    this.goldLine.textContent = uiFormat('Or : {gold}', { gold: uiNumber(side.gold) });
  }

  private stackElement(stack: TradeStack): HTMLElement {
    const template = getItem(stack.templateId);
    const onRemove = this.onRemove;
    const row = document.createElement(onRemove === null ? 'div' : 'button');
    row.className = 'trade__row';
    if (row instanceof HTMLButtonElement) {
      row.type = 'button';
      row.title = uiText('Reprendre');
      row.addEventListener('click', () => {
        onRemove?.(stack.itemId);
      });
    }

    const slot = document.createElement('div');
    slot.className = 'slot';
    const icon = document.createElement('div');
    icon.className = `item item--${template?.rarity ?? 'common'}`;
    paintItemIcon(icon, template, this.icons, stack.templateId);
    addLevelBadge(icon, stack.upgradeLevel);
    slot.append(icon);

    const text = document.createElement('div');
    text.className = 'trade__text';
    const name = document.createElement('span');
    name.className = 'trade__name';
    if (template !== undefined) name.style.color = hex(RARITY_COLORS[template.rarity]);
    const label = template?.name ?? stack.templateId;
    name.textContent = uiText(
      stack.upgradeLevel > 0 ? `${label} +${String(stack.upgradeLevel)}` : label,
    );
    name.title = name.textContent;
    const count = document.createElement('span');
    count.className = 'trade__count';
    count.textContent = uiText(`× ${String(stack.quantity)}`);
    text.append(name, count);
    // The other side sees what it is offered (ADR 0057): a rare's affixes make its worth.
    if (stack.affixes !== undefined && stack.affixes.length > 0) {
      const affixes = document.createElement('span');
      affixes.className = 'trade__affixes';
      affixes.textContent = uiText(stack.affixes.map(describeAffix).join(' · '));
      affixes.title = affixes.textContent;
      text.append(affixes);
    }

    row.append(slot, text);
    return row;
  }
}
